import { CheckCircle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/utils/format/date';
import { formatNumber } from '@/utils/format/number';
import { TooltipAdditionalInfo } from '@/warranty-v3/components/tooltip-additional-info';
import {
  type WarrantyPenalty,
  type WarrantyPenaltyCurrent,
} from '@/warranty-v3/entities/warranty-penalty';
import { usePenaltyDisplay } from '@/warranty-v3/hooks/use-penalty-display';
import { formatPenalty } from '@/warranty-v3/utils/format-penalty';

function EmptyState() {
  return (
    <tr className="border border-green-100 bg-green-50 text-green-800 shadow-inner">
      <td colSpan={6} className="p-6 text-center align-middle">
        <div className="flex flex-col items-center justify-center gap-2">
          <CheckCircle className="size-6 text-green-600" />
          <div className="text-lg font-semibold">
            No penalty records detected
          </div>
          <div className="text-sm text-gray-600">
            All systems are operating within warranty conditions.
          </div>
        </div>
      </td>
    </tr>
  );
}

export type WarrantyPenaltiesTableProps = {
  warrantyPenalties: WarrantyPenalty[];
};

function onlyCurrentPenalty(
  penalty: WarrantyPenalty,
): penalty is WarrantyPenalty & { current: WarrantyPenaltyCurrent } {
  return Boolean(penalty.current);
}

export function WarrantyPenaltiesTable({
  warrantyPenalties,
}: WarrantyPenaltiesTableProps) {
  const penaltyDisplay = usePenaltyDisplay();
  const currentPenalties = warrantyPenalties.filter(onlyCurrentPenalty);

  return (
    <Table striped dense bleed className="w-full table-auto overflow-x-auto">
      <TableHead className="sticky top-0 bg-gray-100">
        <TableRow>
          <TableHeader className="text-left align-bottom">
            Description
          </TableHeader>
          <TableHeader className="text-left align-bottom">
            Extreme value
          </TableHeader>
          <TableHeader className="text-left align-bottom">
            Latest Detect.
          </TableHeader>
          <TableHeader className="text-left align-bottom">Duration</TableHeader>
          <TableHeader className="text-left align-bottom">
            Total Count
          </TableHeader>
          <TableHeader className="text-left align-bottom">
            <TooltipAdditionalInfo content={<>Excluding predictions</>}>
              {penaltyDisplay.label}
            </TooltipAdditionalInfo>
          </TableHeader>
        </TableRow>
      </TableHead>

      <TableBody>
        {currentPenalties.length === 0 ? (
          <EmptyState />
        ) : (
          currentPenalties.map((penalty) => (
            <TableRow key={penalty.id} className="hover:bg-gray-50">
              <TableCell justify="left">{penalty.description}</TableCell>
              <TableCell className="whitespace-nowrap" justify="left">
                {formatNumber(penalty.current.extremeValue, 1)}{' '}
                {penalty.current.extremeValueUnit}
              </TableCell>
              <TableCell justify="left">
                {penalty.current.lastDetectedAt != undefined
                  ? formatDate(penalty.current.lastDetectedAt)
                  : '-'}
              </TableCell>
              <TableCell className="whitespace-nowrap" justify="left">
                {penalty.current.durationHours != undefined
                  ? formatNumber(penalty.current.durationHours, 1)
                  : '-'}
              </TableCell>
              <TableCell justify="left">
                {penalty.current.count != undefined
                  ? penalty.current.count
                  : '-'}
              </TableCell>
              <TableCell justify="left">
                {formatPenalty(penalty.current.penalty, penaltyDisplay.unit)}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
